import { useBatchPilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useParams, useLocation } from "react-router"
import * as Icons from "@nova/component-icons"
import { formatDistance } from "@nova/format"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useMemo } from "react"
import { useDeleteMission } from "features/pilot-tracking/flights-page/PilotTrackingMissionQueries"
import { LinkedLayersCard } from "features/pilot-tracking/missions/linked-layers/LinkedLayersCard"
import { MissionFilesCard } from "features/pilot-tracking/missions/mission-files/MissionFilesCard"
import { FlightPathCard } from "features/pilot-tracking/missions/flight-path/FlightPathCard"
import { SummaryCard } from "features/pilot-tracking/missions/summary/SummaryCard"
// import { TelemetryCard } from "features/pilot-tracking/missions/telemetry/TelemetryCard"
import { DotGraphTelemetryCard } from "features/pilot-tracking/missions/dot-graph-telemetry/DotGraphTelemetryCard"
import { NotesCard } from "features/pilot-tracking/missions/notes/NotesCard"
import { TagsCard } from "features/pilot-tracking/missions/tags/TagsCard"
import {
    missionQuery,
    tagsQuery,
    MissionQuerySingleResult,
    TagsQueryResult,
} from "features/pilot-tracking/missions/MissionPageQueries"
import { getGenericWorkspacePageWithoutHeader } from "../WorkspaceRoutes"
import { useRouter } from "pages"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import {
    getGenericDangerConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { OpenProjectButton } from "features/pilot-tracking/OpenProjectButton"
import { useLinkedLayers } from "features/pilot-tracking/missions/linked-layers/LinkedLayerQueries"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { IconButton } from "@nova/component-btn"
import classNames from "classnames"
import { getDateTimeString } from "@nova/util-strings"
import { MissionOriginRouterState, missionOriginRouterStateSchema } from "@nova/schemas"
import { CustomFieldsCard } from "features/pilot-tracking/missions/linked-layers/CustomFieldsCard"

export const MissionPage = getGenericWorkspacePageWithoutHeader({
    Content: MissionPageContent,
    maxWidth: 1200,
})

function MissionPageContent() {
    const { missionId = "" } = useParams()
    const dispatch = useAppDispatch()
    const modal = useConfirmationModalContext()
    const contextMenu = useAppSelector(selectActiveContextMenu("Mission"))

    const { deleteMission } = useDeleteMission(missionId)
    const router = useRouter("/workspace/:workspaceId")

    const { data } = useBatchPilotTrackingQuery({
        mission: missionQuery(missionId),
        tags: tagsQuery(missionId),
    })
    if (!data) return null
    const response = data.mission[0]
    if (!response) return null

    const handleDeleteClick = async () => {
        dispatch(setActiveContextMenu(undefined))
        void modal.confirm({
            RenderComponent: getGenericDangerConfirmationModalComponent({
                title: "Delete Flight?",
                message:
                    "Deleting the flight removes all data and metrics from tracking, do you want to proceed?",
                dangerMessage: "This cannot be undone.",
            }),
            asyncConfirmCallback: async () => {
                try {
                    await deleteMission()
                    dispatch(
                        setGlobalNotificationContext({
                            message: "1 flight has been deleted.",
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                    router.navigate({ path: "/fleet/flights" })
                } catch {
                    dispatch(
                        setGlobalNotificationContext({
                            message: "Failed to delete 1 flight. Please try again.",
                            status: GlobalNotificationStatus.error,
                            timeoutInMs: 3000,
                        })
                    )
                }
            },
        })
    }

    return (
        <div className="text-gray-100 flex flex-col">
            <MissionPageHeader data={response} missionId={missionId} />
            <MissionPageBody data={response} tags={data.tags} />
            {contextMenu && (
                <ContextMenu
                    sections={[
                        [
                            {
                                title: "Delete",
                                Icon: Icons.Trash,
                                isDestructive: true,
                                onClick: handleDeleteClick,
                            },
                        ],
                    ]}
                    onBlur={() => dispatch(setActiveContextMenu(undefined))}
                    isFixedPosition
                    top={contextMenu.position.top}
                    left={contextMenu.position.left}
                />
            )}
        </div>
    )
}

const MissionPageHeader = ({
    data,
    missionId,
}: {
    data: MissionQuerySingleResult
    missionId: string
}) => {
    const state = useLocation().state
    const origin = missionOriginRouterStateSchema.safeParse(state?.origin)
    const contextMenuOpen = !!useAppSelector(selectActiveContextMenu("Mission"))
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const {
        "metadata.started_at": startedAt,
        "metadata.duration": duration,
        "metadata.distance_flown": distanceFlown,
    } = data
    const durationMinutes = useMemo(() => (duration ? Math.round(+duration / 60) : 0), [duration])
    const distanceFlownFormatted = useMemo(
        () =>
            distanceFlown
                ? formatDistance({ value: +distanceFlown, unit: distanceUnit })
                : formatDistance({ value: 0, unit: distanceUnit }),
        [distanceFlown, distanceUnit]
    )
    const { data: linkedLayers } = useLinkedLayers(missionId)
    const projectIds = useMemo(
        () => [...new Set((linkedLayers ?? []).map((l) => l.projectId))],
        [linkedLayers]
    )

    const openContextMenu = (e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        dispatch(
            setActiveContextMenu({
                type: "Mission",
                data: { missionId },
                position: { top: rect.bottom, left: rect.left - 80 },
            })
        )
    }
    const closeContextMenu = () => {
        dispatch(setActiveContextMenu(undefined))
    }

    const title = useMemo(() => {
        if (!startedAt) return "Unknown date"
        return getDateTimeString(new Date(startedAt))
    }, [startedAt])

    return (
        <div className="flex flex-col">
            <div className="h-[74px] flex items-center gap-3">
                <BackButton origin={origin.success ? origin.data : undefined} />
                <div className="grow" />
                <OpenProjectButton flightId={missionId} projectIds={projectIds} />
                {isEditor && (
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation()
                            if (contextMenuOpen) {
                                closeContextMenu()
                            } else {
                                openContextMenu(e)
                            }
                        }}
                    >
                        <div
                            className={classNames("flex items-center gap-1", {
                                "text-white": contextMenuOpen,
                            })}
                        >
                            <div>Options</div>
                            <Icons.SmallIcon Icon={Icons.ThreeDotsSettings} />
                        </div>
                    </IconButton>
                )}
            </div>
            <div className="flex flex-col gap-1">
                <div className="text-white telegraf text-[25px]">{title}</div>
                <div>
                    {durationMinutes} minutes • {distanceFlownFormatted}
                </div>
            </div>
        </div>
    )
}

const BackButton = ({ origin }: { origin: MissionOriginRouterState | undefined }) => {
    const router = useRouter("/workspace/:workspaceId")
    switch (origin?.page) {
        case "pilot":
            return (
                <router.Link
                    to={{
                        path: "/fleet/pilots/:pilotId",
                        params: { pilotId: origin.pilotId },
                    }}
                >
                    <div className="flex gap-1 items-center hover:text-white transition-colors">
                        <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                        <div>Back to Pilot</div>
                    </div>
                </router.Link>
            )
        case "drone":
            return (
                <router.Link
                    to={{
                        path: "/fleet/aircraft/:aircraftId",
                        params: { aircraftId: origin.droneId },
                    }}
                >
                    <div className="flex gap-1 items-center hover:text-white transition-colors">
                        <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                        <div>Back to Drone</div>
                    </div>
                </router.Link>
            )
        default:
            return (
                <router.Link to={{ path: "/fleet/flights" }}>
                    <div className="flex gap-1 items-center hover:text-white transition-colors">
                        <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                        <div>Back to all flights</div>
                    </div>
                </router.Link>
            )
    }
}

const MissionPageBody = ({
    data,
    tags,
}: {
    data: MissionQuerySingleResult
    tags: TagsQueryResult
}) => {
    const { "metadata.notes": notes } = data
    return (
        <div className="flex flex-col gap-8 py-5">
            <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                    <SummaryCard data={data} />
                    <FlightPathCard />
                </div>
                <DotGraphTelemetryCard />
            </div>

            <div className="flex gap-4 w-full">
                <div className="flex-[1] flex flex-col">
                    <TagsCard tags={tags} />
                </div>
                <div className="flex-[2] flex flex-col">
                    <NotesCard notes={notes} />
                </div>
            </div>

            <CustomFieldsCard />
            <LinkedLayersCard />
            <MissionFilesCard />
        </div>
    )
}
